import React from 'react';
import classes from './Sidebar.module.css';
import SidebarItem from './SidebarItem/SidebarItem';
import diagnosticsIcon from '../../assets/icons/diagnostics-icon.svg';
import salesIcon from '../../assets/icons/sales-icon.svg';
import extendCollapseIcon from '../../assets/icons/back-arrow.svg';
import extendCollapseIcon2 from '../../assets/icons/back-arrow2.svg';
import calendarIcon from '../../assets/icons/calendarSidebarIcon.svg';
import chatIcon from '../../assets/icons/chat-icon.svg';
import customersIcon from '../../assets/icons/customers-icon.svg';
import reportsIcon from '../../assets/icons/reports-icon.svg';
import settingsIcon from '../../assets/icons/settings.svg';

import { useAppSelector } from '../../store/store';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../store/slices/auth';
import { selectNotifications, NotificationObj } from '../../store/slices/notifications';
import { SIDEBAR_ELEMENTS } from './SidebarElements';
import { DOCTOR_USER_ROLE, NURSE_USER_ROLE } from '../../constants/userRolesConstants';
import logo from '../../assets/images/new-logo.svg';
import { useTranslation } from '../../hooks/useTranslations';

interface SidebarProps {
  isExtended: boolean;
  setIsExtended: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isExtended, setIsExtended }) => {
  const { translations } = useTranslation();
  const userRole: string = useAppSelector<string>(selectUserRole);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const notifications = useAppSelector(selectNotifications);
  const chattingNotifications = notifications.filter(
    (el: NotificationObj) => el.type === 'chatting_message_received'
  );
  const unReadedConversations = chattingNotifications.filter(
    (el: NotificationObj, index: number, self: any) =>
      index === self.findIndex((t: any) => t.conversation_id === el.conversation_id)
  ).length;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.sidebarIcon}>
          {!isExtended && <div className={classes.sidebarWithoutOptions} />}
          <img
            src={isExtended ? extendCollapseIcon2 : extendCollapseIcon}
            alt="extend"
            onClick={() => setIsExtended()}
            className={`${isExtended ? classes.collapseIconAbsolute : classes.extendIconAbsolute}`}
          />
        </div>
        {isExtended && (
          <div className={classes.sidebarOptions}>
            <img className={classes.logo} src={logo} alt="ecocare" />
            {userRole === 'admin' &&
              SIDEBAR_ELEMENTS.map((el) => {
                if (!accountInfo.supports_chat && el.label === 'sidebar.chat') return null;
                return (
                  <SidebarItem
                    key={el.label}
                    label={translations(el.label)}
                    icon={el.icon}
                    subItems={el.subItems}
                    setIsExtended={() => setIsExtended()}
                    navigateTo={el.navigateTo}
                    notificationIndicator={
                      el.navigateTo === '/web/chat' ? unReadedConversations : undefined
                    }
                  />
                );
              })}
            {userRole === 'diagnostic employee' && (
              <>
                <SidebarItem
                  label={translations('sidebar.diagnostics')}
                  icon={diagnosticsIcon}
                  subItems={[
                    { label: translations('home.test'), navigateTo: '/web/diagnostics/home-test' },
                  ]}
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('reports')}
                  icon={reportsIcon}
                  subItems={[
                    {
                      label: translations('sidebar.diagnostics'),
                      navigateTo: '/web/diagnostics/reports',
                    },
                  ]}
                  setIsExtended={() => setIsExtended()}
                />
              </>
            )}
            {userRole === DOCTOR_USER_ROLE && (
              <>
                <SidebarItem
                  label={translations('schedule')}
                  icon={calendarIcon}
                  navigateTo="/web/schedule"
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('reports')}
                  icon={reportsIcon}
                  navigateTo="/web/reports"
                  setIsExtended={() => setIsExtended()}
                />
                {accountInfo.supports_chat && (
                  <SidebarItem
                    label={translations('sidebar.chat')}
                    icon={chatIcon}
                    setIsExtended={() => setIsExtended()}
                    navigateTo="/web/chat"
                    notificationIndicator={unReadedConversations}
                  />
                )}
              </>
            )}
            {userRole === 'sales employee' && (
              <>
                <SidebarItem
                  label={translations('customers')}
                  navigateTo={'/web/sales/customers'}
                  icon={customersIcon}
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('sidebar.sales')}
                  navigateTo={'/web/sales/orders'}
                  icon={salesIcon}
                  setIsExtended={() => setIsExtended()}
                />
              </>
            )}
            {userRole === 'care center agent' && (
              <>
                <SidebarItem
                  label={translations('schedule')}
                  icon={calendarIcon}
                  navigateTo="/web/schedule"
                  setIsExtended={() => setIsExtended()}
                />
                {accountInfo.supports_chat && (
                  <SidebarItem
                    label={translations('sidebar.chat')}
                    icon={chatIcon}
                    setIsExtended={() => setIsExtended()}
                  />
                )}
                <SidebarItem
                  label={translations('sidebar.settings')}
                  icon={settingsIcon}
                  subItems={[
                    // {
                    //   label: translations('calendar'),
                    //   navigateTo: '/web/calendar',
                    // },
                    {
                      label: translations('support'),
                      navigateTo: '/web/support',
                    },
                  ]}
                  setIsExtended={() => setIsExtended()}
                />
              </>
            )}
            {userRole === NURSE_USER_ROLE && (
              <>
                <SidebarItem
                  label={translations('schedule')}
                  icon={calendarIcon}
                  navigateTo="/web/schedule"
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('customers')}
                  navigateTo={'/web/sales/customers'}
                  icon={customersIcon}
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('reports')}
                  icon={reportsIcon}
                  navigateTo="/web/reports"
                  setIsExtended={() => setIsExtended()}
                />
                <SidebarItem
                  label={translations('sidebar.sales')}
                  navigateTo={'/web/sales/orders'}
                  icon={salesIcon}
                  setIsExtended={() => setIsExtended()}
                />
                {accountInfo.supports_chat && (
                  <SidebarItem
                    label={translations('sidebar.chat')}
                    icon={chatIcon}
                    setIsExtended={() => setIsExtended()}
                    navigateTo="/web/chat"
                    notificationIndicator={unReadedConversations}
                  />
                )}
                <SidebarItem
                  label={translations('sidebar.settings')}
                  icon={settingsIcon}
                  subItems={[
                    // {
                    //   label: translations('calendar'),
                    //   navigateTo: '/web/calendar',
                    // },
                    {
                      label: translations('support'),
                      navigateTo: '/web/support',
                    },
                  ]}
                  setIsExtended={() => setIsExtended()}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
